import axios from 'axios';
import type { AxiosError } from 'axios';

/**
 * Zentrale Fehler-Auswertung für das CMS.
 *
 * Hintergrund: die catch-Blöcke griffen bisher über `catch (error: any)` direkt
 * auf `error.response.data.message` zu. Unter `strict` liefert ein untypisiertes
 * `catch` aber `unknown` — deshalb bündeln diese Helfer die Zugriffe an einer
 * Stelle und machen sie typsicher. Die Reihenfolge der Auswertung entspricht
 * exakt der, die vorher in den einzelnen catch-Blöcken stand.
 */

// Fallback, wenn sich aus dem Fehler keine Meldung lesen lässt.
//
// Bewusst NICHT exportiert: die Konstante ist die letzte Stufe von `getErrorMessage` und
// wird nur als deren Vorgabewert gebraucht. Aufrufer geben ihren eigenen, sprechenderen
// Fallback mit („Speichern fehlgeschlagen." statt „Ein unbekannter Fehler …"); wer keinen
// mitgibt, bekommt diesen hier über die Vorgabe. Ein Export daneben wäre eine zweite Art,
// an denselben Text zu kommen, ohne die Auswertung zu durchlaufen.
const UNKNOWN_ERROR_MESSAGE = 'Ein unbekannter Fehler ist aufgetreten.';

/** Einzelner Validierungsfehler, wie ihn die API im Feld `errors` liefert. */
export interface ApiValidationError {
  field?: string;
  param?: string;
  message?: string;
  msg?: string;
}

/** Fehler-Body der API. Alle Felder optional — je nach Endpunkt kommt nur eines. */
export interface ApiErrorData {
  message?: string;
  error?: string;
  errors?: ApiValidationError[];
}

/**
 * Prüft, ob es sich um einen Axios-Fehler handelt, und verengt den Typ auf
 * unseren API-Fehler-Body. Nutzt bewusst `axios.isAxiosError` statt eines
 * eigenen Duck-Type-Checks.
 */
export function isAxiosError(e: unknown): e is AxiosError<ApiErrorData> {
  return axios.isAxiosError<ApiErrorData>(e);
}

/** Fehler-Body der Server-Antwort, falls der Server überhaupt geantwortet hat. */
export function getErrorData(e: unknown): ApiErrorData | undefined {
  if (!isAxiosError(e)) return undefined;
  const data = e.response?.data;
  // Ein Fehler-Body kann auch ein String oder HTML sein — dann gibt es nichts auszulesen.
  return typeof data === 'object' && data !== null ? data : undefined;
}

/** HTTP-Status der Fehler-Antwort, falls vorhanden. */
export function getErrorStatus(e: unknown): number | undefined {
  return isAxiosError(e) ? e.response?.status : undefined;
}

/**
 * True, wenn der Request rausging, aber keine Antwort ankam
 * (Netzwerkfehler, Timeout, Server nicht erreichbar).
 */
export function isNoResponseError(e: unknown): boolean {
  return isAxiosError(e) && !e.response && Boolean(e.request);
}

/**
 * Ermittelt die anzuzeigende Fehlermeldung in dieser Reihenfolge:
 * 1. `message` aus dem Fehler-Body der API
 * 2. `error` aus dem Fehler-Body der API
 * 3. `Error.message` (bei Axios z. B. „Request failed with status code 500")
 * 4. der übergebene Fallback
 */
export function getErrorMessage(e: unknown, fallback: string = UNKNOWN_ERROR_MESSAGE): string {
  const data = getErrorData(e);
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/**
 * Baut die Meldung aus den Validierungsfehlern der API
 * (`{ errors: [{ field, message }] }`), falls welche vorliegen.
 */
export function getValidationErrorMessage(e: unknown): string | undefined {
  const errors = getErrorData(e)?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return undefined;
  return errors
    .map((v) => `${v.field || v.param || 'Unbekanntes Feld'}: ${v.message || v.msg}`)
    .join('\n');
}

/** Struktur für `console.error`-Diagnose-Ausgaben. */
export interface ErrorDetails {
  message: string;
  status?: number;
  data?: ApiErrorData;
  stack?: string;
}

/** Bündelt die Felder, die die catch-Blöcke bisher einzeln ins Log geschrieben haben. */
export function getErrorDetails(e: unknown): ErrorDetails {
  return {
    message: getErrorMessage(e),
    status: getErrorStatus(e),
    data: getErrorData(e),
    stack: e instanceof Error ? e.stack : undefined,
  };
}
